import React, { useState, useEffect } from 'react';
import { User, FormMetadata, FormResponse } from '../../types';
import { db } from '@/src/services/db';
import FormResponder from './FormResponder';
import SyncStatus from './SyncStatus';
import { 
  ClipboardList, History, User as UserIcon, QrCode, 
  ChevronRight, CheckCircle2, Clock, AlertCircle, LogOut 
} from 'lucide-react';

interface OperatorAppProps {
  user: User;
  onLogout: () => void;
  syncStatus?: {
      pendingCount: number;
      isSyncing: boolean;
      lastError: string | null;
      onSync: () => void;
  };
}

const OperatorApp: React.FC<OperatorAppProps> = ({ user, onLogout, syncStatus }) => {
  const [activeTab, setActiveTab] = useState<'tasks' | 'history' | 'profile'>('tasks');
  const [forms, setForms] = useState<FormMetadata[]>([]);
  const [history, setHistory] = useState<FormResponse[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    // Load relevant data
    db.getForms().then((allForms) => {
      // Filter to only show forms belonging to the user's sectors
      const userSectors = user.sectorIds || (user.sector ? [user.sector] : []);
      const filteredForms = allForms.filter(f => userSectors.includes(f.sectorId) || userSectors.includes(f.sector));
      setForms(filteredForms);
    });
    db.getUserResponses(user.id).then(setHistory);
  }, [user.id, user.sectorIds, user.sector, selectedFormId]); // Reload history after form submission

  // --- Handlers ---
  const handleScan = () => {
    setIsScanning(true);
    // Simulate scan delay
    setTimeout(() => {
        setIsScanning(false);
        if (forms.length > 0) {
            // Pick the first form for demo purposes
            setSelectedFormId(forms[0].id);
        } else {
            alert("Nenhum formulário encontrado no código QR.");
        }
    }, 1500);
  };

  const handleFormClose = () => {
    setSelectedFormId(null);
    // Refresh history
    db.getUserResponses(user.id).then(setHistory);
  };

  // --- Render: Form Responder Overlay ---
  if (selectedFormId) {
    return <FormResponder formId={selectedFormId} onClose={handleFormClose} />;
  }

  // --- Render: Main Tabs ---
  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      
      {/* SCANNING OVERLAY */}
      {isScanning && (
          <div className="fixed inset-0 bg-black z-[100] flex flex-col items-center justify-center animate-in fade-in">
              <div className="w-64 h-64 border-2 border-white/50 rounded-xl relative overflow-hidden">
                  <div className="absolute inset-0 bg-green-500/20 animate-pulse"></div>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-green-500 shadow-[0_0_20px_rgba(34,197,94,1)] animate-[scan_2s_ease-in-out_infinite]"></div>
              </div>
              <p className="text-white mt-8 font-bold tracking-widest animate-pulse">BUSCANDO QR CODE...</p>
          </div>
      )}

      {/* Tab Content */}
      <div className="max-w-md mx-auto min-h-screen bg-white shadow-xl relative">
        
        {/* TAB 1: TASKS (HOME) */}
        {activeTab === 'tasks' && (
          <div className="animate-in fade-in duration-300">
            {/* Header */}
            <div className="bg-slate-900 text-white p-6 rounded-b-3xl shadow-lg">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">Bem-vindo de volta,</p>
                        <h1 className="text-2xl font-bold">{user.name.split(' ')[0]}</h1>
                        <span className="inline-block mt-2 px-2 py-0.5 bg-indigo-600 text-[10px] font-bold uppercase rounded tracking-wider">
                            {user.sector}
                        </span>
                    </div>
                    <div className="h-10 w-10 bg-slate-800 rounded-full flex items-center justify-center text-sm font-bold border-2 border-slate-700">
                        {user.name.charAt(0)}
                    </div>
                </div>
            </div>

            {/* Task List */}
            <div className="p-4 space-y-4">
                {syncStatus && <SyncStatus {...syncStatus} />}
                <h2 className="text-lg font-bold text-slate-800 px-2">Tarefas Pendentes</h2>
                
                {forms.map(form => (
                    <div 
                        key={form.id}
                        onClick={() => setSelectedFormId(form.id)}
                        className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 active:scale-98 transition-transform flex items-center gap-4 cursor-pointer"
                    >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm ${
                            form.sector === 'Manutenção' ? 'bg-orange-500' : 
                            form.sector === 'Segurança' ? 'bg-blue-500' : 'bg-slate-500'
                        }`}>
                            <ClipboardList size={24} />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-slate-800">{form.title}</h3>
                            <p className="text-xs text-slate-400">v{form.currentVersion} • Não Iniciado</p>
                        </div>
                        <div className="bg-slate-50 p-2 rounded-full text-slate-400">
                            <ChevronRight size={20} />
                        </div>
                    </div>
                ))}

                {forms.length === 0 && (
                    <div className="text-center p-8 text-slate-400">
                        Nenhuma tarefa atribuída. Bom trabalho!
                    </div>
                )}
            </div>

            {/* FAB (Floating Action Button) */}
            <button 
                onClick={handleScan}
                className="fixed bottom-24 right-6 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-indigo-700 active:scale-90 transition-all z-10"
            >
                <QrCode size={24} />
            </button>
          </div>
        )}

        {/* TAB 2: HISTORY */}
        {activeTab === 'history' && (
           <div className="p-6 animate-in fade-in duration-300">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Histórico de Atividade</h2>
                
                <div className="space-y-6 relative border-l-2 border-slate-100 ml-3 pl-6">
                    {history.map(res => (
                        <div key={res.id} className="relative">
                            {/* Timeline Dot */}
                            <div className="absolute -left-[31px] top-0 bg-white p-1">
                                {res.status === 'submitted' ? (
                                    <CheckCircle2 className="text-green-500" size={20} />
                                ) : (
                                    <Clock className="text-amber-500" size={20} />
                                )}
                            </div>
                            
                            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                                <p className="text-xs font-mono text-slate-400 mb-1">
                                    {new Date(res.submittedAt).toLocaleDateString()} • {new Date(res.submittedAt).toLocaleTimeString()}
                                </p>
                                <h3 className="font-bold text-slate-800">{forms.find(f => f.id === res.formId)?.title || 'Formulário Desconhecido'}</h3>
                                <p className="text-xs text-slate-500 mt-1">ID: {res.id.split('-')[1]}</p>
                            </div>
                        </div>
                    ))}
                    {history.length === 0 && <p className="text-slate-400 text-sm">Sem histórico ainda.</p>}
                </div>
           </div>
        )}

        {/* TAB 3: PROFILE */}
        {activeTab === 'profile' && (
            <div className="p-6 animate-in fade-in duration-300 flex flex-col items-center">
                <div className="w-24 h-24 bg-slate-200 rounded-full mb-4 flex items-center justify-center text-3xl font-bold text-slate-500">
                    {user.name.charAt(0)}
                </div>
                <h2 className="text-xl font-bold text-slate-900">{user.name}</h2>
                <p className="text-slate-500 mb-8">{user.role} • {user.sector}</p>

                <div className="w-full space-y-3">
                    <button className="w-full bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between text-slate-700">
                        <span>Status de Sincronização</span>
                        <span className="text-green-600 text-sm font-bold">Online</span>
                    </button>
                    <button className="w-full bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between text-slate-700">
                        <span>Versão do App</span>
                        <span className="text-slate-400 text-sm">v0.2.1</span>
                    </button>
                    <button 
                        onClick={onLogout}
                        className="w-full bg-red-50 p-4 rounded-xl shadow-sm border border-red-100 flex items-center justify-center gap-2 text-red-600 font-bold mt-8"
                    >
                        <LogOut size={20}/> Sair
                    </button>
                </div>
            </div>
        )}

      </div>

      {/* BOTTOM NAVIGATION */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 pb-safe z-50">
        <div className="max-w-md mx-auto flex justify-around p-2">
            <button 
                onClick={() => setActiveTab('tasks')}
                className={`flex-1 flex flex-col items-center p-2 rounded-xl transition-colors ${activeTab === 'tasks' ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
            >
                <ClipboardList size={24} strokeWidth={activeTab === 'tasks' ? 2.5 : 2} />
                <span className="text-[10px] font-medium mt-1">Tarefas</span>
            </button>
            
            <button 
                onClick={() => setActiveTab('history')}
                className={`flex-1 flex flex-col items-center p-2 rounded-xl transition-colors ${activeTab === 'history' ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
            >
                <History size={24} strokeWidth={activeTab === 'history' ? 2.5 : 2} />
                <span className="text-[10px] font-medium mt-1">Histórico</span>
            </button>
            
            <button 
                onClick={() => setActiveTab('profile')}
                className={`flex-1 flex flex-col items-center p-2 rounded-xl transition-colors ${activeTab === 'profile' ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
            >
                <UserIcon size={24} strokeWidth={activeTab === 'profile' ? 2.5 : 2} />
                <span className="text-[10px] font-medium mt-1">Perfil</span>
            </button>
        </div>
      </div>

    </div>
  );
};

export default OperatorApp;